import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const ADLRecordForm = ({
    formData,
    handleChange,
    handleActivitiesChange,
    adlActivities = [],
    isLoading = false,
    errors = {},
    isEdit = false,
    initialActivities = [],
}) => {
    const { user } = useSelector((state) => state.auth);
    const userName = user?.name || "";
    const [activities, setActivities] = useState([]);
    const isInitialized = useRef(false); // Prevent re-init

    // === Initialize ONLY ONCE for Edit mode ===
    useEffect(() => {
        if (isEdit && initialActivities.length > 0 && adlActivities.length > 0 && !isInitialized.current) {
            const grouped = initialActivities.reduce((acc, rec) => {
                const existing = acc.find((a) => a.activity_id === rec.activity_id);
                const recData = {
                    date: rec.date || "",
                    score: String(rec.score || ""),
                    totalScore: rec.totalScore || rec.total_score || "",
                    staffName: rec.staffName || rec.staff_name || "",
                    created_at: rec.created_at || "",
                    id: rec.id || 0,
                };
                if (existing) {
                    existing.records.push(recData);
                } else {
                    const master = adlActivities.find((a) => a.id === rec.activity_id);
                    acc.push({
                        activity_id: rec.activity_id,
                        name: master?.name || "Loading...",
                        records: [recData],
                    });
                }
                return acc;
            }, []);

            const namesFound = grouped.every(g => g.name !== "Loading...");
            if (namesFound) {
                // Sort records in each activity group descending by date (newest first)
                grouped.forEach((activity) => {
                    activity.records.sort((a, b) => {
                        const aDate = a.date ? new Date(a.date).getTime() : 0;
                        const bDate = b.date ? new Date(b.date).getTime() : 0;
                        if (bDate !== aDate) {
                            return bDate - aDate;
                        }
                        const aTime = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
                        const bTime = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
                        return bTime - aTime;
                    });
                });
                setActivities(grouped);
                isInitialized.current = true;
            }
        }
    }, [initialActivities, adlActivities, isEdit]);

    // === Initialize for Add mode (only if not edit) ===
    useEffect(() => {
        if (!isEdit && adlActivities.length > 0 && activities.length === 0) {
            const initialized = adlActivities.map((act) => ({
                activity_id: act.id,
                name: act.name,
                records: [
                    {
                        date: new Date().toISOString().split('T')[0],
                        score: "",
                        totalScore: "",
                        staffName: userName,
                    },
                ],
            }));
            setActivities(initialized);
        }
    }, [adlActivities, isEdit, activities.length, userName]);

    // === Synchronous updater helper to sync both local and parent states ===
    const updateActivitiesAndParent = (newActivities) => {
        const sortedActivities = newActivities.map((act) => {
            const sortedRecords = [...act.records].sort((a, b) => {
                const aDate = a.date ? new Date(a.date).getTime() : 0;
                const bDate = b.date ? new Date(b.date).getTime() : 0;
                if (bDate !== aDate) {
                    return bDate - aDate;
                }
                const aTime = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
                const bTime = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
                return bTime - aTime;
            });
            return { ...act, records: sortedRecords };
        });

        setActivities(sortedActivities);
        const activitiesData = sortedActivities.flatMap((act) =>
            act.records.map((rec) => ({
                activity_id: act.activity_id,
                date: rec.date,
                score: rec.score,
                totalScore: rec.totalScore,
                staffName: rec.staffName || "",
                created_at: rec.created_at,
                id: rec.id,
            }))
        );
        handleActivitiesChange(activitiesData);
    };

    const handleActivityChange = (activityIndex, recordIndex, field, value) => {
        const newActivities = activities.map((act, idx) => {
            if (idx !== activityIndex) return act;
            return {
                ...act,
                records: act.records.map((rec, rIdx) => {
                    if (rIdx !== recordIndex) return rec;
                    const updatedRec = {
                        ...rec,
                        [field]: field === "score" ? String(value) : value,
                    };
                    if (field === "score") {
                        updatedRec.totalScore = String(value);
                    }
                    return updatedRec;
                }),
            };
        });
        updateActivitiesAndParent(newActivities);
    };

    const addRecord = (activityIndex) => {
        const newActivities = activities.map((act, idx) => {
            if (idx !== activityIndex) return act;
            return {
                ...act,
                records: [
                    {
                        date: new Date().toISOString().split('T')[0],
                        score: "",
                        totalScore: "",
                        staffName: userName,
                    },
                    ...act.records,
                ],
            };
        });
        updateActivitiesAndParent(newActivities);
    };

    const removeRecord = (activityIndex, recordIndex) => {
        const newActivities = activities.map((act, idx) => {
            if (idx !== activityIndex) return act;
            const filteredRecords = act.records.filter((_, rIdx) => rIdx !== recordIndex);
            if (filteredRecords.length === 0) return act;
            return {
                ...act,
                records: filteredRecords,
            };
        });
        updateActivitiesAndParent(newActivities);
    };

    if (isLoading && !isEdit) {
        return (
            <div className="d-flex justify-content-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="content p-4">
            <div className="row g-3 justify-content-center">
                {/* Client Info */}
                <div className="col-12 mb-3">
                    <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3">
                        Client Information
                    </small>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className={`form-control ${errors.clientName ? "is-invalid" : ""}`}
                                    name="clientName"
                                    value={formData.clientName || ""}
                                    onChange={handleChange}
                                    placeholder="Client Name"
                                    readOnly={!!formData.patientId || !!formData.patient_id}
                                />
                                <label>Client Name *</label>
                                {errors.clientName && <div className="invalid-feedback">{errors.clientName}</div>}
                            </div>
                        </div>

                        <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className={`form-control ${errors.roomNumber ? "is-invalid" : ""}`}
                                    name="roomNumber"
                                    value={formData.roomNumber || ""}
                                    onChange={handleChange}
                                    placeholder="Room Number"
                                />
                                <label>Room Number *</label>
                                {errors.roomNumber && <div className="invalid-feedback">{errors.roomNumber}</div>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className={`form-control ${errors.dateRange ? "is-invalid" : ""}`}
                                    name="dateRange"
                                    value={formData.dateRange || ""}
                                    onChange={handleChange}
                                    placeholder="Date Range"
                                />
                                <label>Date Range *</label>
                                {errors.dateRange && <div className="invalid-feedback">{errors.dateRange}</div>}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scoring Guide */}
                <div className="col-12">
                    <div className="p-4 bg-primary-subtle rounded-4 mb-3 shadow-sm border border-primary-subtle text-center">
                        <div className="d-flex justify-content-center align-items-center mb-2">
                            <i className="ri-star-line fs-5 text-primary me-2"></i>
                            <h6 className="fw-semibold mb-0">Scoring System</h6>
                        </div>
                        <div className="d-flex justify-content-center flex-wrap gap-4 mt-3 small fw-medium">
                            {[0, 1, 2, 3].map((s) => (
                                <div className="text-center" key={s}>
                                    <span className="badge bg-white text-dark border mb-1">{s}</span>
                                    <div>
                                        <strong>
                                            {s === 0 ? "Not Attempted" : s === 1 ? "Independent" : s === 2 ? "Supervision" : "Dependent"}
                                        </strong>
                                    </div>
                                    <small className="text-muted">
                                        {s === 0 ? "Not Attempted" : s === 1 ? "Performs task alone" : s === 2 ? "Needs verbal guidance" : "Requires full assistance"}
                                    </small>
                                </div>
                            ))}
                        </div>
                        <p className="mb-0 mt-4 text-primary small">
                            Select the appropriate score based on client's functional ability.
                        </p>
                    </div>
                </div>

                {/* Activities Accordion */}
                <div className="col-md-12">
                    <div className="accordion" id="adlActivitiesAccordion">
                        {activities.map((activity, activityIndex) => {
                            const collapseId = `activity-collapse-${activity.activity_id}`;
                            const headingId = `activity-heading-${activity.activity_id}`;

                            return (
                                <div className="accordion-item mb-2 shadow-sm" key={activity.activity_id}>
                                    <h2 className="accordion-header" id={headingId}>
                                        <button
                                            className={`accordion-button ${activityIndex === 0 ? "" : "collapsed"}`}
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#${collapseId}`}
                                            aria-expanded={activityIndex === 0}
                                            aria-controls={collapseId}
                                        >
                                            {activity.name}
                                        </button>
                                    </h2>
                                    <div
                                        id={collapseId}
                                        className={`accordion-collapse collapse ${activityIndex === 0 ? "show" : ""}`}
                                        aria-labelledby={headingId}
                                        data-bs-parent="#adlActivitiesAccordion"
                                    >
                                        <div className="accordion-body mt-2">
                                            {/* Add Record button at the top */}
                                            <div className="mb-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    onClick={() => addRecord(activityIndex)}
                                                >
                                                    <i className="ri-add-line me-1"></i> Add Date Record
                                                </button>
                                            </div>

                                            {activity.records.map((record, recordIndex) => (
                                                <div
                                                    className="row g-2 align-items-center mb-2 border-bottom pb-2"
                                                    key={`${activity.activity_id}-${recordIndex}`}
                                                >
                                                    {/* Date */}
                                                    <div className="col-md-3">
                                                        <div className="form-floating form-floating-outline">
                                                            <input
                                                                type="date"
                                                                className="form-control"
                                                                value={record.date}
                                                                onChange={(e) =>
                                                                    handleActivityChange(
                                                                        activityIndex,
                                                                        recordIndex,
                                                                        "date",
                                                                        e.target.value
                                                                    )
                                                                }
                                                            />
                                                            <label>Date</label>
                                                        </div>
                                                    </div>

                                                    {/* Score Radio */}
                                                    <div className="col-md-3">
                                                        <div className="d-flex justify-content-around">
                                                            {[0, 1, 2, 3].map((score) => (
                                                                <div className="form-check form-check-inline" key={score}>
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="radio"
                                                                        name={`score-${activity.activity_id}-${recordIndex}`}
                                                                        id={`score-${activity.activity_id}-${recordIndex}-${score}`}
                                                                        value={score}
                                                                        checked={record.score === String(score)}
                                                                        onChange={(e) =>
                                                                            handleActivityChange(
                                                                                activityIndex,
                                                                                recordIndex,
                                                                                "score",
                                                                                e.target.value
                                                                            )
                                                                        }
                                                                    />
                                                                    <label
                                                                        className="form-check-label small"
                                                                        htmlFor={`score-${activity.activity_id}-${recordIndex}-${score}`}
                                                                    >
                                                                        {score}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Total Score */}
                                                    <div className="col-md-2">
                                                        <div className="form-floating form-floating-outline">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={record.totalScore}
                                                                onChange={(e) =>
                                                                    handleActivityChange(
                                                                        activityIndex,
                                                                        recordIndex,
                                                                        "totalScore",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Total Score"
                                                            />
                                                            <label>Total Score</label>
                                                        </div>
                                                    </div>

                                                    {/* Staff Name */}
                                                    <div className="col-md-3">
                                                        <div className="form-floating form-floating-outline">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={record.staffName}
                                                                onChange={(e) =>
                                                                    handleActivityChange(
                                                                        activityIndex,
                                                                        recordIndex,
                                                                        "staffName",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                placeholder="Staff Name"
                                                                readOnly
                                                            />
                                                            <label>Staff Name</label>
                                                        </div>
                                                    </div>

                                                    {/* Delete Button */}
                                                    {activity.records.length > 1 && (
                                                        <div className="col-md-1">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm"
                                                                onClick={() => removeRecord(activityIndex, recordIndex)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Checked By */}
                <div className="col-12 mb-3">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    name="checkedBy"
                                    value={formData.checkedBy || ""}
                                    onChange={handleChange}
                                    placeholder="Checked by"
                                />
                                <label>Checked by</label>
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    name="nursingManager"
                                    value={formData.nursingManager || ""}
                                    onChange={handleChange}
                                    placeholder="Nursing Manager/Head Nurse"
                                />
                                <label>Nursing Manager/Head Nurse</label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit */}
                <div className="col-12 d-flex justify-content-end mt-4">
                    <div className="col-md-2 d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                            {isEdit ? (
                                <>
                                    <i className="ri-edit-2-line me-1"></i> Update
                                </>
                            ) : (
                                <>
                                    <i className="ri-save-3-line me-1"></i> Save
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ADLRecordForm;